import type { ColorToken } from '@ui';
import type { FC, ReactNode } from 'react';
import type { PickProps } from '@/ui/types/types';
import clsx from 'clsx';
import { tokenToVariable } from '@/ui/utils/tokenToVariable';
import styles from './Icon.module.css';
import ArrowIcon from './svg/arrow.svg?react';
import BadgeIcon from './svg/badge.svg?react';
import CalendarIcon from './svg/calendar.svg?react';
import CalendarClockIcon from './svg/calendarClock.svg?react';
import CheckIcon from './svg/check.svg?react';
import CheckCircleIcon from './svg/checkCircle.svg?react';
import ChevronDownIcon from './svg/chevronDown.svg?react';
import ClockIcon from './svg/clock.svg?react';
import CloseIcon from './svg/close.svg?react';
import ConstructionIcon from './svg/construction.svg?react';
import DiscordIcon from './svg/discord.svg?react';
import FloodIcon from './svg/flood.svg?react';
import LockOpenIcon from './svg/lockOpen.svg?react';
import LoginIcon from './svg/login.svg?react';
import LogoutIcon from './svg/logout.svg?react';
import NoLuggageIcon from './svg/noluggage.svg?react';

type Props = {
    icon: ReactNode;
    size?: number;
    color?: ColorToken;
    spin?: boolean;
    rotate?: number;
    center?: boolean;
} & PickProps<'span', 'ref' | 'onClick'>;

const IconBase: FC<Props> = ({ icon, size = 32, color = 'iconMain', spin, rotate, center, onClick, ...rest }) => {
    return (
        <span
            role="img"
            className={clsx(styles.icon, spin && styles.iconSpin, center && styles.iconCenter)}
            style={{
                width: `${size}px`,
                height: `${size}px`,
                color: tokenToVariable(color),
                ...(onClick ? { cursor: 'pointer' } : {}),
                ...(rotate ? { transform: `rotate(${rotate}deg)` } : {})
            }}
            onClick={onClick}
            {...rest}
        >
            {icon}
        </span>
    );
};

type PropsWithoutIcon = Omit<Props, 'icon'>;

const Arrow: FC<PropsWithoutIcon> = props => <IconBase icon={<ArrowIcon />} {...props} />;
const Badge: FC<PropsWithoutIcon> = props => <IconBase icon={<BadgeIcon />} {...props} />;
const Calendar: FC<PropsWithoutIcon> = props => <IconBase icon={<CalendarIcon />} {...props} />;
const CalendarClock: FC<PropsWithoutIcon> = props => <IconBase icon={<CalendarClockIcon />} {...props} />;
const Check: FC<PropsWithoutIcon> = props => <IconBase icon={<CheckIcon />} {...props} />;
const CheckCircle: FC<PropsWithoutIcon> = props => <IconBase icon={<CheckCircleIcon />} {...props} />;
const ChevronDown: FC<PropsWithoutIcon> = props => <IconBase icon={<ChevronDownIcon />} {...props} />;
const Clock: FC<PropsWithoutIcon> = props => <IconBase icon={<ClockIcon />} {...props} />;
const Flood: FC<PropsWithoutIcon> = props => <IconBase icon={<FloodIcon />} {...props} />;
const Discord: FC<PropsWithoutIcon> = props => <IconBase icon={<DiscordIcon />} {...props} />;
const Logout: FC<PropsWithoutIcon> = props => <IconBase icon={<LogoutIcon />} {...props} />;
const Login: FC<PropsWithoutIcon> = props => <IconBase icon={<LoginIcon />} {...props} />;
const NoLuggage: FC<PropsWithoutIcon> = props => <IconBase icon={<NoLuggageIcon />} {...props} />;
const Close: FC<PropsWithoutIcon> = props => <IconBase icon={<CloseIcon />} {...props} />;
const LockOpen: FC<PropsWithoutIcon> = props => <IconBase icon={<LockOpenIcon />} {...props} />;
const Construction: FC<PropsWithoutIcon> = props => <IconBase icon={<ConstructionIcon />} {...props} />;

export const Icon = Object.assign(IconBase, {
    Arrow,
    Badge,
    Calendar,
    CalendarClock,
    Check,
    CheckCircle,
    ChevronDown,
    Clock,
    Flood,
    Discord,
    Logout,
    NoLuggage,
    Close,
    Login,
    LockOpen,
    Construction
});
